/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import { Table, Modal, Tooltip } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import InspectionLogTableCell from './InspectionLogTableCell';
import { getProjectTableLoadingConfig } from './ProjectTableLoader';
import {
  REJECT_CELL_COLOR,
  findTestDefinitionForColumn,
  getColumnTitleText,
  formatWeldColumnLabel,
  getTableColumnsForModal,
  getTableDataForModal,
  getTableRowRangeStatus,
  isTableMultipleComparatorMode,
  parseJsonCellValue,
  parseTestTableData,
} from '../utils/inspectionLogCell';
import { PqtMandatoryTestLabel, isPqtTestMandatory } from '../utils/pqtTestLabel';

const STATUS_ROW_KEYS = new Set(['repair_status', 'overall_status']);

/** Pivot log API rows (weld × test columns) → process-description rows × weld columns. */
export function buildTransposedRowsFromPivot(dataList, columnList, stationTests) {
  if (!Array.isArray(dataList) || dataList.length === 0) {
    return { rows: [], weldIds: [] };
  }

  const weldIds = dataList.map((r) => r.weld_id).filter((id) => id != null && String(id).trim() !== '');

  const testColumns = (columnList || []).filter((col) => {
    const di = col.dataIndex || col.key;
    const title = col.columnTitle || getColumnTitleText(col);
    return (
      di !== 'weld_id' &&
      di !== 'repair_status' &&
      di !== 'overall_status' &&
      title !== 'Weld #' &&
      title !== 'Weld' &&
      title !== 'Repair Status' &&
      title !== 'Overall Status'
    );
  });

  const rows = testColumns.map((col) => {
    const title = col.columnTitle || getColumnTitleText(col);
    const testDef = findTestDefinitionForColumn(
      { ...col, title, columnTitle: title },
      stationTests
    );
    const dataIndex = col.dataIndex || col.key || title;
    const row = {
      key: dataIndex,
      process_description: title,
      mandatory: testDef?.mandatory,
      _testDef: testDef,
      _dataIndex: dataIndex,
    };
    dataList.forEach((weldRow) => {
      const weldId = weldRow.weld_id;
      if (weldId) row[weldId] = weldRow[dataIndex];
    });
    return row;
  });

  ['repair_status', 'overall_status'].forEach((param) => {
    const row = {
      key: param,
      process_description: param.replace(/_/g, ' ').toUpperCase(),
      mandatory: false,
      _testDef: null,
      _dataIndex: param,
    };
    dataList.forEach((weldRow) => {
      if (weldRow.weld_id) row[weldRow.weld_id] = weldRow[param];
    });
    rows.push(row);
  });

  return { rows, weldIds };
}

const InspectionLogTransposedTable = ({
  dataList,
  columnList,
  stationTests,
  testTypesList,
  loading,
}) => {
  const [tableResultItem, setTableResultItem] = useState(null);

  const { rows, weldIds } = useMemo(
    () => buildTransposedRowsFromPivot(dataList, columnList, stationTests),
    [dataList, columnList, stationTests]
  );

  const columns = useMemo(
    () => [
      {
        title: 'Process Description',
        dataIndex: 'process_description',
        key: 'process_description',
        width: 220,
        fixed: 'left',
        render: (text, record) => (
          <Tooltip
            title={
              isPqtTestMandatory(record.mandatory)
                ? `${text} (mandatory test)`
                : text
            }
          >
            <span className="pqt-log-process-description">
              <PqtMandatoryTestLabel label={text} mandatory={record.mandatory} />
            </span>
          </Tooltip>
        ),
      },
      ...weldIds.map((weldId) => ({
        title: (
          <Tooltip placement="topLeft" title={weldId}>
            <span className="log-weld-column-header">{formatWeldColumnLabel(weldId)}</span>
          </Tooltip>
        ),
        dataIndex: weldId,
        key: weldId,
        align: 'center',
        width: 100,
        render: (text, record) => (
          <InspectionLogTableCell
            text={text}
            column={{
              dataIndex: STATUS_ROW_KEYS.has(record.key) ? record.key : record._dataIndex,
            }}
            testDef={record._testDef}
            testTypesList={testTypesList}
            onOpenTable={setTableResultItem}
          />
        ),
      })),
    ],
    [weldIds, testTypesList]
  );

  const tableModalColumns = useMemo(() => {
    const cols = getTableColumnsForModal(tableResultItem);
    if (!tableResultItem?.value) return cols;

    const parsedValueData = parseJsonCellValue(tableResultItem.value);
    if (!parsedValueData?.rows) return cols;

    return cols.map((col) => {
      if (col.dataIndex === 'name' && isTableMultipleComparatorMode(tableResultItem)) {
        return {
          ...col,
          render: (text, record) => {
            const rowIndex = record.key;
            const valueRow = parsedValueData?.rows?.[rowIndex];
            const templateRow = parseTestTableData(tableResultItem)?.rows?.[rowIndex];
            const rowStatus = getTableRowRangeStatus(valueRow, templateRow, tableResultItem);
            if (rowStatus === false) {
              return <span style={{ color: REJECT_CELL_COLOR }}>{text}</span>;
            }
            return text;
          },
        };
      }
      return col;
    });
  }, [tableResultItem]);

  const tableModalData = useMemo(
    () => getTableDataForModal(tableResultItem),
    [tableResultItem]
  );

  const hasData = rows.length > 0 && weldIds.length > 0;

  return (
    <>
      <div className="pqt-results-table-shell log-inspection-transposed-table">
        {hasData ? (
          <Table
            className="log-transposed-table"
            columns={columns}
            dataSource={rows}
            loading={getProjectTableLoadingConfig(loading)}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        ) : (
          <Table
            className="log-transposed-table"
            loading={getProjectTableLoadingConfig(loading)}
            pagination={false}
          />
        )}
      </div>

      <Modal
        width="min(92vw, 720px)"
        wrapClassName="log-table-results-modal-wrap"
        className="log-table-results-modal"
        title="Table Results"
        centered
        zIndex={10020}
        getContainer={() => document.body}
        visible={Boolean(tableResultItem)}
        onCancel={() => setTableResultItem(null)}
        maskStyle={{ background: 'rgba(0, 0, 0, 0.45)' }}
        footer={[
          <CrceButton
            key="close"
            label="Close"
            variant="tertiary"
            size="medium"
            onClick={() => setTableResultItem(null)}
          />,
        ]}
      >
        <Table
          columns={tableModalColumns}
          dataSource={tableModalData}
          pagination={false}
          scroll={{ x: 'max-content' }}
          className="log-table-results-inner-table"
        />
      </Modal>
    </>
  );
};

export default InspectionLogTransposedTable;
